import type { HourColumn } from 'constants/export';
import { COLUMN_ESTIMATED_INTERNAL_COST, COLUMN_INTERNAL_COST } from 'constants/filters';
import { DateTime, Interval } from 'luxon';
import type { CompanySizes } from 'redux/modules/accounts.schema';
import { DeprecatedCompanySizes } from 'redux/modules/accounts.schema';
import type { Account, BoxFeature, SubscriptionPlan } from 'types/account';
import { AccountStatus } from 'types/account';

export function filterHourColumnOptions(
  order: Array<HourColumn>,
  permissions: { hideInternalRate: boolean; hasTasks: boolean }
) {
  let filteredOrder = [...order];
  if (permissions.hideInternalRate) {
    filteredOrder = filteredOrder.filter(
      column => column !== COLUMN_INTERNAL_COST && column !== COLUMN_ESTIMATED_INTERNAL_COST
    );
  }

  return filteredOrder;
}

export function convertDeprecatedCompanySizes(
  companySize: CompanySizes | `${DeprecatedCompanySizes}`
): CompanySizes {
  if (companySize === DeprecatedCompanySizes.JUST_ME) {
    return 1;
  } else if (companySize === DeprecatedCompanySizes.LESS_THAN_TWENTY) {
    return 10;
  } else if (companySize === DeprecatedCompanySizes.MORE_THAN_TWENTY) {
    return 50;
  }
  return companySize;
}

export const getAccumulatedFeaturesByPlan = (plans: Array<SubscriptionPlan>) => {
  if (!plans || !plans.length) return null;

  // monthly & yearly plans have the same feature sets, so filter out yearly to avoid duplication
  const sortedMonthlyPlans = plans
    .filter(plan => plan.billing_cycle === 'yearly')
    .sort((a, b) => a.price_monthly - b.price_monthly);

  return sortedMonthlyPlans.reduce<Record<string, Array<BoxFeature>>>(
    (featuresAccumulator, currentPlan) => {
      const accumulatedFeatures = Object.entries<Array<BoxFeature>>(featuresAccumulator).reduce<
        Array<BoxFeature>
      >((previousPlanFeatures, currentPlanFeatures) => {
        const [_, value] = currentPlanFeatures;
        return [...previousPlanFeatures, ...value];
      }, []);

      const dedupedAccumulatedFeatures = [
        ...new Map(accumulatedFeatures.map(feature => [feature.id, feature])).values(),
      ];

      return {
        ...featuresAccumulator,
        [currentPlan.name]: [...dedupedAccumulatedFeatures, ...currentPlan.box_features],
      };
    },
    {}
  );
};

export function getDaysSinceAccountExpired(account: Account) {
  const hasExpiredStatus =
    account.status === AccountStatus.expired || account.status === AccountStatus.exceeded;

  if (!hasExpiredStatus) {
    return null;
  }

  const paymentDue = DateTime.fromISO(account.next_charge);
  const daysSincePaymentDue = Interval.fromDateTimes(paymentDue, DateTime.now().startOf('day'));

  return Math.floor(daysSincePaymentDue.length('days'));
}
